/* global BigInt */
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { Connection } from '@solana/web3.js';
import { db } from '../firebaseConfig';
import { getPaymentNetwork } from './paymentNetwork';
import { isVerifiedPayment, timestampMs } from './activityEvents';
import feeMath from '../../functions/serviceFeePolicy.js';
import { DONATION_TOKENS } from './donationTokens';

// Preserve the existing pre-receipt record format. Donation history is now
// server-write-only; new payments always carry verification and intent fields.
// Historical access does not relabel old entries as server-verified financial proof.
const inflight = new Map();
const snapshotRows = snapshot => {
    const rows = [];
    snapshot?.forEach(doc => rows.push({ ...doc.data(), id: doc.id }));
    return rows;
};
function matches(record, options) {
    return record.status === 'confirmed' && String(record.compId || record.componentId || '') === String(options.componentId) &&
        (!options.sender || record.sender === options.sender) &&
        (!record.spaceOwnerUid || record.spaceOwnerUid === options.spaceOwnerUid) &&
        (!record.pageOwnerId || record.pageOwnerId === options.spaceOwnerUid) &&
        (!options.recipient || record.receiver === options.recipient) &&
        (!options.currency || (record.currency || 'SOL') === options.currency) &&
        (options.tokenMint == null || (record.tokenMint || '') === options.tokenMint);
}
function historicalCredit(record, cluster) {
    if (['verificationVersion', 'verificationOrigin', 'requestId'].some(field => Object.hasOwn(record, field))) return null;
    const createdAt = timestampMs(record.createdAt);
    if (!(createdAt > 0 && createdAt <= Date.now()) ||
        typeof record.txSig !== 'string' || !/^[1-9A-HJ-NP-Za-km-z]{85,88}$/.test(record.txSig) ||
        !record.sender || !record.receiver || (record.cluster && record.cluster !== cluster)) return null;
    const token = DONATION_TOKENS.find(item => item.symbol === (record.currency || 'SOL'));
    const decimals = record.decimals ?? token?.decimals;
    if (!Number.isInteger(decimals) || decimals < 0 || decimals > 18) return null;
    try {
        const amountRaw = feeMath.decimalToAtomic(record.amount, decimals);
        if (BigInt(amountRaw) <= 0n || (record.amountRaw != null && record.amountRaw !== amountRaw)) return null;
        return { ...record, amount: Number(amountRaw) / 10 ** decimals, amountRaw, decimals, creditSource: 'historical' };
    } catch { return null; }
}
export function mergeDonationRecords(receipts, history, options) {
    const result = [];
    // A signature already represented by the ledger must never earn legacy credit too.
    const claimed = new Set(receipts.map(record => record.txSig).filter(Boolean));
    const included = new Set();
    for (const record of receipts) {
        if (!matches(record, options) || record.kind !== 'donation' || record.spaceOwnerUid !== options.spaceOwnerUid ||
            !isVerifiedPayment(record) || !Number.isInteger(record.decimals) || record.decimals < 0 || record.decimals > 18 || !options.cluster || record.cluster !== options.cluster || included.has(record.txSig)) continue;
        included.add(record.txSig);
        result.push({ ...record, amount: Number(record.amountRaw) / 10 ** record.decimals, creditSource: 'receipt' });
    }
    for (const record of history) {
        if (!matches(record, options) || claimed.has(record.txSig) || included.has(record.txSig)) continue;
        const credit = historicalCredit(record, options.cluster);
        if (!credit) continue;
        included.add(record.txSig);
        result.push(credit);
    }
    return result.sort((left, right) => timestampMs(right.createdAt) - timestampMs(left.createdAt));
}
export function donationCreditAtLeast(records, threshold) {
    try {
        const required = BigInt(feeMath.decimalToAtomic(threshold, 18));
        if (required <= 0n) return false;
        const total = records.reduce((sum, record) => sum + BigInt(record.amountRaw) * 10n ** BigInt(18 - record.decimals), 0n);
        return total >= required;
    } catch { return false; }
}
function donationQueries(options) {
    const common = [where('compId', '==', String(options.componentId)), where('status', '==', 'confirmed')];
    if (options.sender) common.push(where('sender', '==', options.sender));
    return [query(collection(db, 'paymentReceipts'), where('kind', '==', 'donation'),
        where('spaceOwnerUid', '==', options.spaceOwnerUid), ...common),
    query(collection(db, 'donations'), ...common)];
}
export function readDonationRecords(options, endpoint) {
    const key = JSON.stringify([options, endpoint]);
    if (inflight.has(key)) return inflight.get(key);
    const pending = (async () => {
        const snapshots = await Promise.all(donationQueries(options).map(reference => getDocs(reference)));
        const [receipts, history] = snapshots.map(snapshotRows);
        let cluster = '';
        // Historical access never required RPC availability; keep that contract.
        // Modern receipts still require the existing network and proof checks.
        if (receipts.some(isVerifiedPayment)) {
            try { cluster = await getPaymentNetwork(new Connection(endpoint, 'confirmed')); } catch { /* No modern credit without a network check. */ }
        }
        return mergeDonationRecords(receipts, history, { ...options, cluster });
    })().finally(() => inflight.delete(key));
    inflight.set(key, pending);
    return pending;
}
export function subscribeDonationRecords(options, onChange, onError) {
    let receipts = [], history = [];
    const emit = () => onChange(mergeDonationRecords(receipts, history, options));
    const [receiptQuery, historyQuery] = donationQueries(options);
    const stopReceipts = onSnapshot(receiptQuery, snapshot => { receipts = snapshotRows(snapshot); emit(); }, onError);
    const stopHistory = onSnapshot(historyQuery, snapshot => { history = snapshotRows(snapshot); emit(); }, onError);
    return () => { stopReceipts(); stopHistory(); };
}
